import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { IoMdArrowDropdown } from 'react-icons/io';
import TagSelecionada from './TagSelecionada';

/**
 * Substitui a fileira de "cartões" (chips clicáveis, um por ano) que
 * dashboards com múltiplos anos usavam — mesma mecânica do
 * `CompanyMultiSelect` (Popover + checkbox + tags removíveis), sem o campo de
 * busca porque a lista de anos é curta o bastante pra não precisar filtrar.
 */
const YearMultiSelect = ({ years = [], value = [], onChange, isDisabled = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const selecionados = useMemo(
    () => (value || []).map(Number).filter((ano) => !Number.isNaN(ano)),
    [value]
  );

  const alternar = (ano) => {
    const proximo = selecionados.includes(ano)
      ? selecionados.filter((atual) => atual !== ano)
      : [...selecionados, ano].sort((a, b) => a - b);
    onChange(proximo);
  };

  if (years.length === 0) return null;

  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement='bottom-start'
        matchWidth
        isLazy
        // SEM Portal, e isto é o conserto de um bug: este seletor vive
        // DENTRO de um Drawer, e o Drawer do Chakra prende o foco no
        // próprio conteúdo. Painel portalado para fora fica fora dessa
        // trava — o popover abria e não dava para marcar nada.
        // `strategy='fixed'` sozinho já tira o painel do contexto de
        // rolagem, que era a única razão do Portal. Mesma correção que o
        // CompanyMultiSelect já tinha recebido.
        strategy='fixed'
      >
        <PopoverTrigger>
          <Button
            w='100%'
            h='32px'
            px='12px'
            bg='white'
            border='1px solid'
            borderColor='gray.200'
            borderRadius='8px'
            fontSize='sm'
            fontWeight={500}
            color={selecionados.length ? 'black' : 'gray.500'}
            _hover={{ bg: 'white' }}
            _active={{ bg: 'white' }}
            isDisabled={isDisabled}
            rightIcon={<IoMdArrowDropdown fontSize='20px' opacity={0.5} />}
          >
            <Text flex='1' textAlign='left' noOfLines={1}>
              {selecionados.length
                ? `${selecionados.length} ano${selecionados.length > 1 ? 's' : ''}`
                : 'Selecione os anos'}
            </Text>
          </Button>
        </PopoverTrigger>

          <PopoverContent w='auto' minW='140px' borderRadius='8px' _focusVisible={{ outline: 'none' }}>
            <PopoverBody p='8px'>
              <Flex justify='space-between' align='center' mb='6px' px='2px'>
                <Box
                  as='button'
                  type='button'
                  fontSize='11px'
                  fontWeight={600}
                  color='#0B4A8F'
                  onClick={() => onChange([...years].sort((a, b) => a - b))}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Marcar todos
                </Box>
                <Box
                  as='button'
                  type='button'
                  fontSize='11px'
                  fontWeight={600}
                  color='#8A94A6'
                  onClick={() => onChange([])}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Limpar
                </Box>
              </Flex>

              {years.map((ano) => (
                <Box key={ano} px='8px' py='6px' borderRadius='6px' _hover={{ bg: '#F4F6F8' }}>
                  <Checkbox isChecked={selecionados.includes(ano)} onChange={() => alternar(ano)}>
                    <Text fontSize='sm'>{ano}</Text>
                  </Checkbox>
                </Box>
              ))}
            </PopoverBody>
          </PopoverContent>
      </Popover>

      {selecionados.length > 0 && (
        <Flex gap='6px' wrap='wrap' mt='8px'>
          {selecionados.map((ano) => (
            <TagSelecionada
              key={ano}
              label={String(ano)}
              onRemove={() => alternar(ano)}
              isDisabled={isDisabled}
              maxW='120px'
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};

export default YearMultiSelect;
